// The TipTap schema for SkillVerse rich text — every node, mark and behaviour
// the editor understands — kept free of React so it can be shared.
//
// Two editors use it: the admin panel's RichTextEditor, and the Trainer Portal's
// note editor (built from this file into a standalone script; see
// scripts/build-trainer-note-editor.mjs). They MUST share one definition. HTML
// is only preserved by an editor whose schema knows every element in it — the
// trainer app previously edited these same notes in Quill, which, on a save
// with no changes at all, stripped fonts, rebuilt tables, rewrote code blocks
// and deleted embedded video. A second copy of this list would drift the same way.
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { TableMap, selectionCell, isInTable } from "@tiptap/pm/tables";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Superscript from "@tiptap/extension-superscript";
import Subscript from "@tiptap/extension-subscript";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import { TextStyle } from "@tiptap/extension-text-style";
import FontFamily from "@tiptap/extension-font-family";
import Color from "@tiptap/extension-color";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { createLowlight, common } from "lowlight";

export const lowlight = createLowlight(common);

// Curated language list for the toolbar dropdown — lowlight's `common` bundle
// registers all of these (and more) for live-editor highlighting.
export const CODE_LANGUAGES = [
  { value: "plaintext", label: "Plain text" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "python", label: "Python" },
  { value: "java", label: "Java" },
  { value: "c", label: "C" },
  { value: "cpp", label: "C++" },
  { value: "csharp", label: "C#" },
  { value: "xml", label: "HTML/XML" },
  { value: "css", label: "CSS" },
  { value: "json", label: "JSON" },
  { value: "sql", label: "SQL" },
  { value: "bash", label: "Bash/Shell" },
  { value: "markdown", label: "Markdown" },
];

export const FONT_OPTIONS = [
  { value: "", label: "Font" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Times New Roman', serif", label: "Times New Roman" },
  { value: "'Courier New', monospace", label: "Courier New" },
  { value: "'Trebuchet MS', sans-serif", label: "Trebuchet" },
];

export const IMAGE_WIDTHS = ["25%", "50%", "75%", "100%"];

// Text colour palette, shown as a grid in the toolbar. Rows run light-to-dark
// within a hue so a lighter tint is always one step up from its base colour.
export const TEXT_COLORS = [
  { value: "#0f172a", label: "Black" },   { value: "#475569", label: "Slate" },
  { value: "#94a3b8", label: "Grey" },    { value: "#ffffff", label: "White" },
  { value: "#dc2626", label: "Red" },     { value: "#ea580c", label: "Orange" },
  { value: "#ca8a04", label: "Amber" },   { value: "#16a34a", label: "Green" },
  { value: "#0d9488", label: "Teal" },    { value: "#2563eb", label: "Blue" },
  { value: "#4f46e5", label: "Indigo" },  { value: "#9333ea", label: "Purple" },
  { value: "#db2777", label: "Pink" },    { value: "#7f1d1d", label: "Maroon" },
  { value: "#14532d", label: "Dark green" }, { value: "#1e3a8a", label: "Navy" },
];

// The table-size picker: a grid of this many columns × rows to hover over, plus
// exact inputs for anything larger, capped at the second pair.
export const TABLE_PICKER = { cols: 10, rows: 8, maxCols: 20, maxRows: 50 };

export const MIN_ROW_HEIGHT = 24;   // px
export const MIN_COL_WIDTH = 40;    // px

// ── Custom Image extension: adds width + align attributes ─────────────────────
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (el) => el.getAttribute("width") || el.style.width || null,
        renderHTML: (attrs) =>
          attrs.width ? { width: attrs.width, style: `width:${attrs.width};max-width:100%;` } : {},
      },
      align: {
        default: "none",
        parseHTML: (el) => el.getAttribute("data-align") || "none",
        renderHTML: (attrs) =>
          attrs.align && attrs.align !== "none" ? { "data-align": attrs.align } : {},
      },
    };
  },
});

// ── VideoEmbed: renders YouTube as <iframe>, other URLs as <video> ────────────
export function extractYouTubeId(url) {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

const VideoEmbed = Node.create({
  name: "videoEmbed",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      src:  { default: null },
      kind: { default: "video" }, // "youtube" | "video"
    };
  },

  parseHTML() {
    return [{ tag: "div[data-video-embed]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const { src, kind } = HTMLAttributes;
    const inner = kind === "youtube"
      ? ["iframe", { src, frameborder: "0", allowfullscreen: "true",
          style: "width:100%;aspect-ratio:16/9;border:none;" }]
      : ["video", { src, controls: "true", style: "width:100%;max-width:100%;" }];
    return ["div", mergeAttributes({ "data-video-embed": "true" }), inner];
  },

  addNodeView() {
    return ({ node }) => {
      const wrapper = document.createElement("div");
      wrapper.setAttribute("data-video-embed", "true");
      wrapper.style.cssText = "width:100%;margin:0.5em 0;";
      const { src, kind } = node.attrs;
      if (kind === "youtube") {
        const iframe = document.createElement("iframe");
        iframe.src = src;
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("allowfullscreen", "true");
        iframe.style.cssText = "width:100%;aspect-ratio:16/9;border:none;display:block;";
        wrapper.appendChild(iframe);
      } else {
        const video = document.createElement("video");
        video.src = src;
        video.controls = true;
        video.style.cssText = "width:100%;max-width:100%;display:block;";
        wrapper.appendChild(video);
      }
      return { dom: wrapper };
    };
  },

  addCommands() {
    return {
      insertVideo: (attrs) => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs }),
    };
  },
});

// ── Compress image to JPEG blob (reduces file size before upload) ─────────────
function compressToBlob(file, maxWidth = 1400, quality = 0.82) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => resolve(blob || file),
        "image/jpeg",
        quality,
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// ── Upload image to server, returns the hosted URL ────────────────────────────
// Where images are uploaded. Set by the host: the admin panel passes its
// REACT_APP_API_URL, the trainer editor the admin API origin.
let API_BASE = "";
export const setUploadBase = (base) => { API_BASE = base || ""; };

export async function uploadImage(file) {
  // GIFs must not be re-encoded to JPEG — that kills the animation
  const isGif = file.type === "image/gif";
  const blob = isGif ? file : await compressToBlob(file);
  const filename = isGif ? "image.gif" : "image.jpg";
  const formData = new FormData();
  formData.append("image", blob, filename);
  const res = await fetch(`${API_BASE}/api/upload-image`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) throw new Error(`Image upload failed (${res.status})`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message || "Upload failed");
  return data.url;
}

const VIDEO_URL_REGEX = /^https?:\/\/.+\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i;

// ── Extension: intercepts paste + drop of image/video files and video URLs ────
const ImagePasteHandler = Extension.create({
  name: "imagePasteHandler",
  addProseMirrorPlugins() {
    const { editor } = this;
    return [
      new Plugin({
        props: {
          handlePaste(_view, event) {
            const items = Array.from(event.clipboardData?.items || []);

            // Video file pasted — can't upload through Lambda (10 MB limit)
            if (items.some((i) => i.type.startsWith("video/"))) {
              event.preventDefault();
              window.alert(
                "Video files cannot be pasted directly.\n\nUse the video button (🎬) in the toolbar to embed a YouTube link or a direct video URL."
              );
              return true;
            }

            // Plain-text paste — detect video / YouTube URLs and auto-embed
            const text = event.clipboardData?.getData("text/plain")?.trim();
            if (text) {
              const ytId = extractYouTubeId(text);
              if (ytId) {
                event.preventDefault();
                editor.chain().focus().insertVideo({
                  src: `https://www.youtube.com/embed/${ytId}`,
                  kind: "youtube",
                }).run();
                return true;
              }
              if (VIDEO_URL_REGEX.test(text)) {
                event.preventDefault();
                editor.chain().focus().insertVideo({ src: text, kind: "video" }).run();
                return true;
              }
            }

            // Image file pasted
            const imageItems = items.filter((item) => item.type.startsWith("image/"));
            if (!imageItems.length) return false;
            event.preventDefault();
            imageItems.forEach((item) => {
              const file = item.getAsFile();
              if (!file) return;
              uploadImage(file)
                .then((src) => editor.chain().focus().setImage({ src }).run())
                .catch((err) => console.error("Image upload failed:", err));
            });
            return true;
          },
          handleDrop(_view, event, _slice, moved) {
            if (moved) return false;
            const files = Array.from(event.dataTransfer?.files || []);

            // Video file dropped
            if (files.some((f) => f.type.startsWith("video/"))) {
              event.preventDefault();
              window.alert(
                "Video files cannot be dropped here.\n\nUse the video button (🎬) in the toolbar to embed a YouTube link or a direct video URL."
              );
              return true;
            }

            const imageFiles = files.filter((f) => f.type.startsWith("image/"));
            if (!imageFiles.length) return false;
            event.preventDefault();
            imageFiles.forEach((file) => {
              uploadImage(file)
                .then((src) => editor.chain().focus().setImage({ src }).run())
                .catch((err) => console.error("Image upload failed:", err));
            });
            return true;
          },
        },
      }),
    ];
  },
});

// ── Table rows with a height ──────────────────────────────────────────────────
// Stored as px on the <tr> ("height: 48px"), which every browser treats as the
// row's minimum height — content taller than that still fits. Only px values
// are read back; a row authored elsewhere in other units keeps its natural size.
const SizedTableRow = TableRow.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      height: {
        default: null,
        parseHTML: (el) => {
          const m = /^(\d+(?:\.\d+)?)px$/.exec((el.style.height || "").trim());
          return m ? Math.round(parseFloat(m[1])) : null;
        },
        renderHTML: (attrs) => (attrs.height ? { style: `height: ${attrs.height}px` } : {}),
      },
    };
  },
});

// The cell the selection is in, with its table, the table's position and map.
function currentTableCell(state) {
  if (!isInTable(state)) return null;
  const $cell = selectionCell(state);
  const table = $cell.node(-1);
  const tableStart = $cell.start(-1);
  const map = TableMap.get(table);
  const col = map.colCount($cell.pos - tableStart);
  return { $cell, table, tableStart, map, col };
}

// The tableRow node around the selection, and its position.
function currentTableRow(state) {
  const { $from } = state.selection;
  for (let d = $from.depth; d > 0; d -= 1) {
    if ($from.node(d).type.name === "tableRow") return { node: $from.node(d), pos: $from.before(d) };
  }
  return null;
}

// Width of one column, written into every cell of it — the same place the
// column-drag handles keep it (each cell's colwidth array, one entry per
// spanned column). null clears it back to automatic.
function writeColumnWidth(tr, { table, tableStart, map }, col, width) {
  for (let row = 0; row < map.height; row += 1) {
    const mapIndex = row * map.width + col;
    // A cell spanning several rows is visited once.
    if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) continue;
    const pos = map.map[mapIndex];
    const cell = table.nodeAt(pos);
    const { colspan } = cell.attrs;
    const index = col - map.colCount(pos);
    const colwidth = cell.attrs.colwidth ? cell.attrs.colwidth.slice() : new Array(colspan).fill(0);
    colwidth[index] = width || 0;
    tr.setNodeMarkup(tableStart + pos, null, {
      ...cell.attrs,
      colwidth: colwidth.some((w) => w > 0) ? colwidth : null,
    });
  }
}

// ── Table sizing commands ────────────────────────────────────────────────────
// Typed sizes for the row/column the cursor is in, used by the table bar. The
// drag handles (column: built into TipTap's table; row: RowResizing below)
// write the same attributes, so either way of resizing shows in the other.
const TableSizing = Extension.create({
  name: "tableSizing",
  addCommands() {
    return {
      setRowHeight: (height) => ({ state, tr, dispatch }) => {
        const row = currentTableRow(state);
        if (!row) return false;
        const h = height ? Math.max(MIN_ROW_HEIGHT, Math.round(height)) : null;
        if (dispatch) tr.setNodeMarkup(row.pos, null, { ...row.node.attrs, height: h });
        return true;
      },
      setColumnWidth: (width) => ({ state, tr, dispatch }) => {
        const info = currentTableCell(state);
        if (!info) return false;
        const w = width ? Math.max(MIN_COL_WIDTH, Math.round(width)) : null;
        if (dispatch) writeColumnWidth(tr, info, info.col, w);
        return true;
      },
      // Clears every column width and row height, so the table goes back to
      // filling the page width with evenly sized columns.
      resetTableSize: () => ({ state, tr, dispatch }) => {
        const info = currentTableCell(state);
        if (!info) return false;
        if (dispatch) {
          for (let col = 0; col < info.map.width; col += 1) writeColumnWidth(tr, info, col, null);
          info.table.forEach((row, offset) => {
            if (row.attrs.height) tr.setNodeMarkup(info.tableStart + offset, null, { ...row.attrs, height: null });
          });
        }
        return true;
      },
    };
  },
});

// Current sizes, for showing in the table bar's inputs. Null = automatic.
export function getTableSizes(editor) {
  const { state } = editor;
  const info = currentTableCell(state);
  if (!info) return { rowHeight: null, colWidth: null };
  // info.col is the cell's own first column, so its width is colwidth[0].
  return {
    rowHeight: currentTableRow(state)?.node.attrs.height || null,
    colWidth: info.$cell.nodeAfter?.attrs.colwidth?.[0] || null,
  };
}

// ── Row resizing by drag ──────────────────────────────────────────────────────
// TipTap ships column resizing but not rows. This mirrors it: hovering the
// bottom edge of a cell shows a resize cursor, and dragging sets that row's
// height. The row is previewed live in the DOM and written to the document
// once, on release, so the whole drag is one undo step.
const ROW_HANDLE_PX = 5;
const rowResizingKey = new PluginKey("rowResizing");

function rowEdgeAt(view, event) {
  const cell = event.target?.closest?.("td, th");
  if (!cell || !view.dom.contains(cell) || cell.rowSpan > 1) return null;
  const rect = cell.getBoundingClientRect();
  if (event.clientY < rect.bottom - ROW_HANDLE_PX || event.clientY > rect.bottom + 1) return null;
  let pos;
  try { pos = view.posAtDOM(cell, 0); } catch { return null; }
  const $pos = view.state.doc.resolve(pos);
  for (let d = $pos.depth; d > 0; d -= 1) {
    if ($pos.node(d).type.name === "tableRow") return { pos: $pos.before(d), dom: cell.parentElement };
  }
  return null;
}

const RowResizing = Extension.create({
  name: "rowResizing",
  addProseMirrorPlugins() {
    let dragging = false;
    return [
      new Plugin({
        key: rowResizingKey,
        props: {
          handleDOMEvents: {
            mousemove(view, event) {
              if (dragging || !view.editable) return false;
              view.dom.classList.toggle("row-resize-cursor", !!rowEdgeAt(view, event));
              return false;
            },
            mouseleave(view) {
              if (!dragging) view.dom.classList.remove("row-resize-cursor");
              return false;
            },
            mousedown(view, event) {
              if (!view.editable || event.button !== 0) return false;
              const hit = rowEdgeAt(view, event);
              if (!hit) return false;
              event.preventDefault();
              dragging = true;
              const startY = event.clientY;
              const startHeight = hit.dom.getBoundingClientRect().height;
              const previousStyle = hit.dom.style.height;
              const heightAt = (y) => Math.round(Math.max(MIN_ROW_HEIGHT, startHeight + y - startY));
              const onMove = (e) => { hit.dom.style.height = `${heightAt(e.clientY)}px`; };
              const onUp = (e) => {
                window.removeEventListener("mousemove", onMove);
                window.removeEventListener("mouseup", onUp);
                dragging = false;
                view.dom.classList.remove("row-resize-cursor");
                const node = view.state.doc.nodeAt(hit.pos);
                if (Math.abs(e.clientY - startY) < 2 || node?.type.name !== "tableRow") {
                  hit.dom.style.height = previousStyle;    // a click, not a drag
                  return;
                }
                view.dispatch(view.state.tr.setNodeMarkup(hit.pos, null, { ...node.attrs, height: heightAt(e.clientY) }));
              };
              window.addEventListener("mousemove", onMove);
              window.addEventListener("mouseup", onUp);
              return true;
            },
          },
        },
      }),
    ];
  },
});

// ── Format painter ────────────────────────────────────────────────────────────
// "Copy formatting": take the character formatting at the cursor (bold,
// italic, underline, strike, super/subscript, colour, font) and lay it over
// the next text the author selects — replacing that text's own formatting, as
// a word processor's format painter does. Links are content, not formatting,
// and are left alone.
//
// One copy paints once. copyFormat({ sticky: true }) — a double-click on the
// toolbar button — keeps painting until cancelled (button again, or Escape).
const formatPainterKey = new PluginKey("formatPainter");
const NOT_FORMATTING = new Set(["link", "code"]);

export const FormatPainter = Extension.create({
  name: "formatPainter",

  addStorage() {
    return { marks: null, sticky: false };
  },

  addCommands() {
    const ext = this;
    // Toolbars re-read their state on a transaction; storage changes alone do
    // not cause one, so every change here is announced with an empty one.
    const announce = (tr, dispatch) => { if (dispatch) dispatch(tr.setMeta(formatPainterKey, true)); };
    return {
      copyFormat: ({ sticky = false } = {}) => ({ state, tr, dispatch }) => {
        const { from, to, empty, $from } = state.selection;
        let marks = empty ? (state.storedMarks || $from.marks()) : null;
        if (!marks) {
          state.doc.nodesBetween(from, to, (node) => {
            if (!marks && node.isText) marks = node.marks;
            return !marks;
          });
        }
        ext.storage.marks = (marks || []).filter((m) => !NOT_FORMATTING.has(m.type.name));
        ext.storage.sticky = sticky;
        announce(tr, dispatch);
        return true;
      },
      cancelFormatPainter: () => ({ tr, dispatch }) => {
        ext.storage.marks = null;
        ext.storage.sticky = false;
        announce(tr, dispatch);
        return true;
      },
      applyCopiedFormat: () => ({ state, tr, dispatch }) => {
        const marks = ext.storage.marks;
        const { from, to, empty } = state.selection;
        if (!marks || empty) return false;
        if (dispatch) {
          Object.values(state.schema.marks).forEach((type) => {
            if (!NOT_FORMATTING.has(type.name)) tr.removeMark(from, to, type);
          });
          marks.forEach((mark) => tr.addMark(from, to, mark));
          if (!ext.storage.sticky) ext.storage.marks = null;
          dispatch(tr.setMeta(formatPainterKey, true));
        }
        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Escape: () => (this.storage.marks ? this.editor.commands.cancelFormatPainter() : false),
    };
  },

  addProseMirrorPlugins() {
    const ext = this;
    // After the mouse (or Shift, for a keyboard selection) is released, the
    // selection is final — that is the moment to paint.
    const paintSoon = () => setTimeout(() => {
      if (ext.storage.marks && !ext.editor.isDestroyed) ext.editor.commands.applyCopiedFormat();
    }, 0);
    return [
      new Plugin({
        key: formatPainterKey,
        props: {
          attributes: () => (ext.storage.marks ? { class: "format-painter-active" } : {}),
          handleDOMEvents: {
            mouseup: () => { if (ext.storage.marks) paintSoon(); return false; },
            keyup: (_view, event) => { if (ext.storage.marks && event.key === "Shift") paintSoon(); return false; },
          },
        },
      }),
    ];
  },
});

export const isFormatPainterActive = (editor) => !!editor?.storage?.formatPainter?.marks;

// ── Extension lists ───────────────────────────────────────────────────────────
export const FULL_EXTENSIONS = [
  StarterKit.configure({ heading: { levels: [1, 2, 3, 4] }, codeBlock: false }),
  CodeBlockLowlight.configure({ lowlight }),
  Underline,
  Superscript,
  Subscript,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
  CustomImage.configure({ inline: false }),
  VideoEmbed,
  ImagePasteHandler,
  // resizable: drag a column border to set its width. Serialised HTML is the
  // same either way (the <colgroup> widths are written regardless), so turning
  // this on changes no existing table. cellMinWidth stays at TipTap's default:
  // it is written into every table's <col> min-widths, so changing it would
  // rewrite every table already saved.
  Table.configure({ resizable: true }),
  SizedTableRow,
  TableCell,
  TableHeader,
  TableSizing,
  RowResizing,
  TextStyle,
  FontFamily,
  Color,
  FormatPainter,
];

export const BASIC_EXTENSIONS = [
  StarterKit.configure({ codeBlock: false }),
  CodeBlockLowlight.configure({ lowlight }),
  Underline,
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
  TextStyle,
];
